// Single application state store. This is the ONE source of truth the whole UI
// reads from and writes through -- no screen keeps its own copy of sites,
// selected site, or transactions.
//
// localStorage is used for exactly one thing: the selected site ID (harmless UI
// state). No financial data is ever written to localStorage. Session persistence
// is handled separately by the Supabase client itself (src/lib/supabase.js).

const SELECTED_SITE_KEY = 'afitexp_selected_site_id';

const state = {
  authStatus: 'loading', // 'loading' | 'signed_out' | 'signed_in'
  session: null,
  profile: null, // { id, full_name, role }
  sites: [],
  sitesLoaded: false,
  sitesError: false,
  projectHealth: [], // company-wide, all sites -- NOT filtered by selectedSiteId
  projectHealthLoading: false,
  projectHealthError: false,
  dashboardTransactions: [], // company-wide Recent Transactions for the Dashboard -- NOT filtered by selectedSiteId
  dashboardTransactionsLoading: false,
  dashboardTransactionsError: false,
  homeDateFilter: 'month', // 'today' | 'month' | 'lastMonth' | 'year' | 'all' -- Home screen's own date filter
  selectedSiteId: readSelectedSiteId(),
  recentTransactions: [],
  recentTransactionsLoading: false,
  activeScreen: 'home', // 'home' | 'materials' | 'labour'
  materialRecords: [],
  materialRecordsLoading: false,
  labourRecords: [],
  labourRecordsLoading: false,
  activeReport: null, // null (defaults to 'overview') | 'overview' | 'income' | 'expense' | 'category' | 'transactions' | 'monthly' | 'material' | 'labour'
  reportDateFrom: currentMonthStart(),
  reportDateTo: currentMonthEnd(),
  reportsTxPage: 1 // current page of the Reports > Detailed Transactions table
};

function currentMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function currentMonthEnd() {
  const d = new Date();
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

const listeners = new Set();

function readSelectedSiteId() {
  try {
    return localStorage.getItem(SELECTED_SITE_KEY) || null;
  } catch {
    return null;
  }
}

function writeSelectedSiteId(id) {
  try {
    if (id) localStorage.setItem(SELECTED_SITE_KEY, id);
    else localStorage.removeItem(SELECTED_SITE_KEY);
  } catch {
    // localStorage unavailable (private mode etc) -- selection just won't survive refresh.
  }
}

export function getState() {
  return state;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setState(patch) {
  Object.assign(state, patch);
  if (Object.prototype.hasOwnProperty.call(patch, 'selectedSiteId')) {
    writeSelectedSiteId(patch.selectedSiteId);
  }
  for (const fn of listeners) fn(state);
}
